import { IDynamicLayerDataParams } from "@/types/map";
import Axios from "axios";
import { loadModules } from "esri-loader";

interface ILayerConfig {
  id: string;
  group: string;
  source: string;
  idField?: string;
  minScale: number;
  maxScale: number;
  infoTemplate: {
    title: string;
    content: string;
  };
  renderer?: any;
}

export default class DynamicIssuesect {
  private static intances: Map<string, DynamicIssuesect>;
  public static getInstance(view: __esri.View) {
    const id = view.container.id;
    if (!DynamicIssuesect.intances) {
      DynamicIssuesect.intances = new Map();
    }
    let instance = DynamicIssuesect.intances.get(id);
    if (!instance) {
      instance = new DynamicIssuesect(view);
      DynamicIssuesect.intances.set(id, instance);
    }
    return instance;
  }

  private view: __esri.View;
  constructor(view: __esri.View) {
    this.view = view;
  }

  private initialized = false;
  private Graphic!: __esri.GraphicConstructor;
  private FeatureLayer!: __esri.FeatureLayerConstructor;
  private layerMap: Map<string, __esri.FeatureLayer[]> = new Map();

  public async initialize(configFile: string) {
    type MapModules = [
      typeof import("esri/Graphic"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/renderers/support/jsonUtils")
    ];
    const [Graphic, FeatureLayer, rendererJsonUtils] = await (loadModules([
      "esri/Graphic",
      "esri/layers/FeatureLayer",
      "esri/renderers/support/jsonUtils",
    ]) as Promise<MapModules>);
    this.Graphic = Graphic;
    this.FeatureLayer = FeatureLayer;

    const response = await Axios.get(configFile);
    if (response.status === 200) {
      console.log(`${configFile}载入成功`);

      const config = response.data;
      const { layers, renderer } = config;

      for (const layerConfig of layers) {
        let layerRenderer = rendererJsonUtils.fromJSON(renderer);
        if (layerConfig.renderer) {
          layerRenderer = rendererJsonUtils.fromJSON(layerConfig.renderer);
        }
        const layer = await this.createLayer(layerConfig, layerRenderer);
        if (layer) {
          if (!this.layerMap.has(layerConfig.group)) {
            this.layerMap.set(layerConfig.group, [layer]);
          } else {
            this.layerMap.get(layerConfig.group)?.push(layer);
          }
        }
      }

      this.initialized = true;
    }
  }

  private async createLayer(
    layerConfig: ILayerConfig,
    renderer: __esri.Renderer
  ): Promise<__esri.FeatureLayer | undefined> {
    const response = await Axios.get(layerConfig.source);
    if (response.status === 200) {
      console.log(`${layerConfig.source}载入成功`);

      const layerData = response.data;

      layerData.fields.push(
        {
          name: "id",
          type: "string",
          alias: "id",
        },
        {
          name: "data",
          type: "string",
          alias: "data",
        },
        {
          name: "type",
          type: "string",
          alias: "type",
        }
      );
      const source = layerData.features.map((feature: any) => {
        const graphic = this.Graphic.fromJSON(feature);
        if (layerConfig.idField) {
          graphic.setAttribute("id", feature.attributes[layerConfig.idField]);
          graphic.setAttribute("type", layerConfig.group);
        }
        return graphic;
      });

      layerData.fields.forEach((field: any) => {
        field.type = field.type.replace("esriFieldType", "").toLowerCase();
      });

      const geometryType:
        | "polygon"
        | "point"
        | "polyline"
        | "multipoint"
        | "multipatch"
        | "mesh"
        | undefined = layerData.geometryType
        .replace("esriGeometry", "")
        .toLowerCase();

      const layer = new this.FeatureLayer({
        source,
        renderer,
        geometryType,
        spatialReference: layerData.spatialReference,
        fields: layerData.fields,
        objectIdField: "FID",
        outFields: ["*"],
        minScale: layerConfig.minScale,
        maxScale: layerConfig.maxScale,
        id: layerConfig.id,
        popupTemplate: layerConfig.infoTemplate,
        visible: false,
      });
      this.view.map.add(layer);

      return layer;
    } else {
    }
  }

  public async showDynamicRendererLayer(
    params: IDynamicLayerDataParams
  ): Promise<void> {
    if (!this.initialized) {
      return;
    }

    const defaultData = params.defaultData || "free";

    const layers = this.layerMap.get(params.name);
    if (layers) {
      layers.forEach(async (layer) => {
        layer.visible = true;

        const graphics = (await layer.queryFeatures()).features;
        const updateGraphics: __esri.Graphic[] = [];

        graphics.forEach((graphic) => {
          const id = graphic.getAttribute("id");
          if (params.datas) {
            const newData = params.datas.find((data) => data.id === id);
            if (newData) {
              graphic.setAttribute("data", newData.data);
              updateGraphics.push(graphic);
            } else if (graphic.getAttribute("data") !== defaultData) {
              graphic.setAttribute("data", defaultData);
              updateGraphics.push(graphic);
            }
          } else if (graphic.getAttribute("data") !== defaultData) {
            graphic.setAttribute("data", defaultData);
            updateGraphics.push(graphic);
          }
        });
        await layer.applyEdits({ updateFeatures: updateGraphics });
      });
    }
  }

  public hideDynamicRendererLayer(params: IDynamicLayerDataParams): void {
    if (!this.initialized) {
      return;
    }

    const layers = this.layerMap.get(params.name);
    if (layers) {
      layers.forEach((layer) => {
        layer.visible = false;
      });
    }
  }
}
